import { useSyncExternalStore } from 'react';

/**
 * The service worker keeps the app itself (code, database engine, PDF engine) on this computer,
 * so LabView opens and works with no internet. New versions wait until the person agrees.
 *
 * Updating has to feel certain: pressing "Update now" shows that it is working, reloads even when
 * the browser does not announce the switch, and, if the new version still does not take over,
 * offers a clean start that leaves the lab's data alone (it is in the database, not in the app's
 * cached files).
 */
export interface PwaState {
  /** The app has been saved for offline use. */
  offlineReady: boolean;
  /** A newer version has downloaded and is waiting. */
  updateReady: boolean;
  /** "Update now" was pressed and the switch is in progress. */
  updating: boolean;
  /** The last update did not finish: show the way out. */
  updateStuck: boolean;
}

let state: PwaState = {
  offlineReady: false,
  updateReady: false,
  updating: false,
  updateStuck: false,
};
const listeners = new Set<() => void>();
let applyUpdate: ((onTakeover: () => void) => void) | undefined;
let registration: ServiceWorkerRegistration | undefined;

function set(next: Partial<PwaState>) {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

const TRIED_KEY = 'qr.update.tried';
const triedRecently = () => {
  try {
    return Date.now() - Number(sessionStorage.getItem(TRIED_KEY) ?? 0) < 90_000;
  } catch {
    return false;
  }
};

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;
  const { Workbox } = await import('workbox-window');
  const wb = new Workbox('/sw.js');
  const announce = (apply: (onTakeover: () => void) => void) => {
    applyUpdate = apply;
    // An update that was just tried and is back again did not finish.
    set({ updateReady: true, updateStuck: triedRecently() });
  };
  wb.addEventListener('waiting', () => {
    announce((onTakeover) => {
      wb.addEventListener('controlling', onTakeover);
      wb.messageSkipWaiting();
    });
  });
  wb.addEventListener('activated', (e) => {
    // First install: the app is now saved for offline use.
    if (!e.isUpdate) {
      set({ offlineReady: true });
      return;
    }
    // A new version took over while this page was open but not controlled by the old one, so it
    // never "waited": the page is still running the old code and needs a reload.
    if (!state.updating) announce((onTakeover) => onTakeover());
  });
  try {
    const reg = await wb.register();
    registration = reg;
    if (reg?.active && !reg.waiting) set({ offlineReady: true });
    const check = () => void reg?.update().catch(() => undefined);
    // Look for a new version now and then while the app stays open all day, and when the person
    // comes back to the window.
    setInterval(check, 30 * 60 * 1000);
    let last = Date.now();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && Date.now() - last > 5 * 60 * 1000) {
        last = Date.now();
        check();
      }
    });
  } catch (err) {
    console.warn('[pwa] could not register the service worker', err);
  }
}

function reloadNow() {
  try {
    sessionStorage.setItem(TRIED_KEY, String(Date.now()));
  } catch {
    // The page just reloads without remembering.
  }
  window.location.reload();
}

/** "Update now": switch to the new version and reload, whatever the browser does or does not report. */
export function installUpdate() {
  if (state.updating) return;
  set({ updating: true });
  // If the browser never reports the switch, reload anyway after a moment.
  const fallback = setTimeout(reloadNow, 2500);
  const takeover = () => {
    clearTimeout(fallback);
    reloadNow();
  };
  if (applyUpdate) applyUpdate(takeover);
  else takeover();
}

/**
 * The way out when an update will not take: remove the app's saved files and the worker, then load
 * LabView again from the internet. The lab's data is not touched; it lives in the database.
 */
export async function startFresh() {
  set({ updating: true });
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    for (const k of await caches.keys()) await caches.delete(k);
  } catch {
    // Reload anyway; the next load fetches the files again.
  }
  window.location.reload();
}

export function usePwa(): PwaState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}

/** For tests and the console: what the page believes. */
export const pwaRegistration = () => registration;
